import { useCallback } from 'react'
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { FirebaseError } from 'firebase/app'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme, type ThemePreference } from '@/contexts/ThemeContext'
import { db } from '@/lib/firebase/config'

const THEME_PREFERENCE_ORDER: ThemePreference[] = ['light', 'dark', 'system']

export function getNextThemePreference(preference: ThemePreference): ThemePreference {
  const currentIndex = THEME_PREFERENCE_ORDER.indexOf(preference)
  return THEME_PREFERENCE_ORDER[(currentIndex + 1) % THEME_PREFERENCE_ORDER.length]
}

export function formatThemePreferenceLabel(
  preference: ThemePreference,
  resolvedTheme: 'light' | 'dark'
) {
  if (preference === 'system') {
    return `System (${resolvedTheme})`
  }

  return preference === 'light' ? 'Light' : 'Dark'
}

export function useThemePreferenceControls() {
  const { user, refreshUser } = useAuth()
  const { themePreference, resolvedTheme, setThemePreference } = useTheme()

  const saveThemePreference = useCallback(async (nextPreference: ThemePreference) => {
    if (nextPreference === themePreference) return true

    setThemePreference(nextPreference)

    if (!user) return true

    try {
      const userRef = doc(db, 'users', user.id || user.uid)
      await updateDoc(userRef, {
        themePreference: nextPreference,
        updatedAt: serverTimestamp(),
      })
      await refreshUser?.()
      return true
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        return true
      } else {
        console.error('Error saving theme preference:', error)
      }
      return false
    }
  }, [refreshUser, setThemePreference, themePreference, user])

  const cycleThemePreference = useCallback(async () => {
    return saveThemePreference(getNextThemePreference(themePreference))
  }, [saveThemePreference, themePreference])

  return {
    themePreference,
    resolvedTheme,
    saveThemePreference,
    cycleThemePreference,
    nextThemePreference: getNextThemePreference(themePreference),
  }
}
